import { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
)

import { makeStyles } from '@material-ui/styles'
import { Card, CardContent, Divider, Typography } from '@material-ui/core'
import palette from 'theme/palette'
import { BootstrapTooltip } from 'components/BootstrapTooltip'
import {
  getDisabledFieldMessage,
  getReportValueDisabledReason,
  isFieldDisabled
} from 'utils/reportDisabledFields'

import ConversionByHour from './ConversionByHour'
import { ChartLegend, ChartTab, ChartTabList, ChartTabs } from './styles'

const useStyles = makeStyles(() => ({
  chartContainer: {
    height: 400,
    position: 'relative'
  },
  actions: {
    justifyContent: 'flex-end'
  }
}))

// Ícones desenhados inline para bater com o traço do design (stroke 2,
// currentColor), em vez dos TwoTone preenchidos do Material.
const TrendIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M3 17l6-6 4 4 7-7' />
    <path d='M14 8h6v6' />
  </svg>
)

const ClockIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7v5l3 2' />
  </svg>
)

const GOAL_TAB = 'goal'
const CONVERSION_TAB = 'conversion'

// A aba escolhida fica salva no navegador para abrir igual na próxima visita.
// O try/catch cobre navegador que bloqueia o storage (aba anônima, cookies
// desligados): aí só não lembra, sem quebrar a tela.
const TAB_STORAGE_KEY = '@NeoPro:reportChartTab'

const loadSavedTab = () => {
  try {
    const saved = localStorage.getItem(TAB_STORAGE_KEY)
    return saved === GOAL_TAB || saved === CONVERSION_TAB ? saved : null
  } catch {
    return null
  }
}

const saveTab = tab => {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    // Sem storage, a escolha vale só enquanto a tela estiver aberta.
  }
}

const DayGoals = ({ data, range, ...rest }) => {
  const classes = useStyles()
  // Começa pela aba salva; null = o usuário nunca escolheu. A padrão não pode
  // ser fixada aqui porque o relatório (e a conversão) chega depois da montagem.
  const [tab, setTab] = useState(loadSavedTab)

  const selectTab = selected => {
    setTab(selected)
    saveTab(selected)
  }

  // A aba de conversão só existe para loja com integração de contagem, e é a
  // que abre por padrão quando existe.
  const conversion = data?.store?.conversion
  const currentTab = conversion ? tab ?? CONVERSION_TAB : GOAL_TAB

  const disabledReason = getReportValueDisabledReason(
    data?.disabledFields || {},
    'goalGraphPercent'
  )
  const disabled = isFieldDisabled(disabledReason)
  const randomNum = () => Math.floor(Math.random() * (235 - 52 + 1) + 52)
  const randomRGB = () => `rgb(${randomNum()}, ${randomNum()}, ${randomNum()})`

  const chartInstance = useRef()

  useEffect(() => {
    // if (chartInstance?.current?.data?.datasets) {
    //   chartInstance.current.data.datasets.forEach((ds, i) => {
    //     if (i >= 4) ds.hidden = true;
    //   });
    //   chartInstance.current.update();
    // }
  }, [chartInstance?.current?.data?.datasets])

  return (
    <Card {...rest}>
      <ChartTabs>
        <ChartTabList>
          <ChartTab
            active={currentTab === GOAL_TAB}
            onClick={() => selectTab(GOAL_TAB)}
          >
            <TrendIcon />
            PERCENTUAL DA META
          </ChartTab>
          {conversion && (
            <ChartTab
              active={currentTab === CONVERSION_TAB}
              onClick={() => selectTab(CONVERSION_TAB)}
            >
              <ClockIcon />
              CONVERSÃO POR HORÁRIO
            </ChartTab>
          )}
        </ChartTabList>

        {currentTab === CONVERSION_TAB && (
          <ChartLegend>
            <div className='item'>
              <span className='box' style={{ backgroundColor: '#1f2fa8' }} />
              Acima da média
            </div>
            <div className='item'>
              <span className='box' style={{ backgroundColor: '#8f98e0' }} />
              Abaixo da média
            </div>
            <div className='item'>
              <span className='dash' />
              Média do período
            </div>
          </ChartLegend>
        )}
      </ChartTabs>
      <Divider />
      <CardContent>
        {/* A aba de conversão tem dado próprio e não passa pelo
            disabledFields, que vale só para o gráfico de meta. */}
        {currentTab === CONVERSION_TAB ? (
          <ConversionByHour conversion={conversion} range={range} />
        ) : data && !disabled ? (
          <div className={classes.chartContainer}>
            <Line
              ref={chartInstance}
              data={{
                labels: data.store.graphValues.dates,
                datasets: [
                  {
                    label: 'Loja',
                    data: data.store.graphValues.percentages.map(item =>
                      (item * 100).toFixed(2)
                    ),
                    fill: false,
                    tension: 0.3,
                    borderColor: palette.primary.main,
                    borderWidth: 3
                  },
                  ...data.sellers.map(s => ({
                    label: s.name.complete,
                    data: s.graph?.percentages.map(item =>
                      item === 0 ? 'N/A' : (item * 100).toFixed(2)
                    ),
                    fill: false,
                    tension: 0.3,
                    borderColor: randomRGB(),
                    borderWidth: 1
                  }))
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                    position: 'right',
                    onHover: (e, item) => {
                      item.fontColor = palette.primary.main
                      chartInstance.current.data.datasets.forEach((ds, i) => {
                        if (i === item.datasetIndex) {
                          ds.borderWidth = 3
                        } else {
                          ds.backupColor = ds.borderColor
                          ds.borderColor = '#FFFFFF50'
                        }
                      })
                      // chartInstance.current.data.datasets[item.datasetIndex].borderWidth = 3
                      chartInstance.current.update()
                    },
                    onLeave: (e, item) => {
                      item.fontColor = '#000'
                      chartInstance.current.data.datasets.forEach((ds, i) => {
                        if (ds.backupColor) {
                          ds.borderColor = ds.backupColor
                        }
                        if (i > 0) {
                          ds.borderWidth = 1
                        }
                      })
                      chartInstance.current.update()
                    }
                  },
                  tooltip: {
                    enabled: true,
                    mode: 'nearest',
                    intersect: false,
                    borderWidth: 1,
                    borderColor: palette.divider,
                    backgroundColor: palette.white,
                    titleColor: palette.text.primary,
                    bodyColor: palette.text.secondary,
                    footerColor: palette.text.secondary,
                    filter: item => item.raw > 0,
                    itemSort: (a, b) => b.raw - a.raw,
                    callbacks: {
                      label: item => `${item.dataset.label}: ${item.raw}%`
                    }
                  },
                  annotation: {
                    annotations: {
                      line1: {
                        type: 'line',
                        yMin: 100,
                        yMax: 100,
                        borderColor: '#FF000030',
                        borderWidth: 2
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    ticks: {
                      callback: (value, index, ticks) => value + '%'
                    },
                    suggestedMin: 0
                  }
                },
                maintainAspectRatio: false,
                interaction: {
                  intersect: false
                },
                layout: { padding: 0 }
              }}
            />
          </div>
        ) : disabled ? (
          <BootstrapTooltip title={getDisabledFieldMessage(disabledReason)}>
            <Typography variant='subtitle2'>-</Typography>
          </BootstrapTooltip>
        ) : (
          <Typography variant='subtitle2'>
            Sem relatório para apresentar.
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

DayGoals.propTypes = {
  className: PropTypes.string
}

export default DayGoals
